import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { CalendarClock, ChefHat, ChevronRight, CircleCheck, Grid3X3, Hourglass, PackageX } from "lucide-react";
import { providerApi, type DailyCloseoutReportRecord } from "../../../lib/api";
import { useAppPreferences } from "../../../lib/preferences";
import { ShiftPill, VenueStatusPill } from "../../provider-shift/TodayCards";
import { subscribeShiftChanged } from "../../provider-shift/useCashShift";
import { getBookingStatusChipTone } from "../../provider-bookings/booking-utils";
import { ProviderReportsCard } from "../ProviderReportsCard";
import { formatLabel, resolveProviderMembershipRole } from "../provider-workspace-helpers";
import { useProviderWorkspace } from "../ProviderWorkspaceContext";
import { useProviderWorkspaceForm } from "../ProviderWorkspaceFormContext";
import { useIncomingBookings } from "../useIncomingBookings";
import { useFoodOrderQueue } from "../../provider-orders/useFoodOrderQueue";
import { useVenueMenu } from "../../provider-orders/useVenueMenu";
import { SLOW_ORDER_MINUTES, formatEgp, minutesSince } from "../../provider-orders/order-status";

/** How many upcoming bookings Home lists before linking to Bookings. */
const COMING_UP_LIMIT = 5;
/** Item names shown in an attention row before "+N more". */
const NAMES_SHOWN = 2;

type Translate = (key: string, values?: Record<string, string | number>) => string;

function namesWithMore(names: string[], t: Translate) {
  const shown = names.slice(0, NAMES_SHOWN).join(", ");
  return names.length > NAMES_SHOWN ? t("{names} +{count} more", { names: shown, count: names.length - NAMES_SHOWN }) : shown;
}

interface AttentionRow {
  key: string;
  icon: typeof ChefHat;
  tone: "danger" | "warning";
  title: string;
  detail?: string;
  action: string;
  onAction: () => void;
}

export function DashboardSection() {
  const navigate = useNavigate();
  const { language } = useAppPreferences();
  const {
    venues,
    summary,
    user,
    isLoading,
    loadError,
    reload,
    openCreateVenue,
    selectedVenue,
    sessions,
    openCreateBooking,
    openWalkIn,
    isOnline,
    pendingConfirmationCount,
    navigateToSection,
    t,
  } = useProviderWorkspace();
  const { setPendingReviewToken } = useProviderWorkspaceForm();

  const { bookings: incomingBookings, isInitialLoading: incomingLoading } = useIncomingBookings(selectedVenue);
  const orderQueue = useFoodOrderQueue(selectedVenue?.id ?? null, { includeRecent: false });
  const menu = useVenueMenu(selectedVenue?.id ?? null);
  const money = useTodayMoney(selectedVenue?.id ?? null);
  const [moneyOpen, setMoneyOpen] = useState(false);

  const soldOut = menu.items.filter((item) => item.sold_out);
  const lowStock = menu.items.filter((item) => item.low_stock);
  const openOrders = orderQueue.orders.filter((order) => order.status === "pending" || order.status === "preparing" || order.status === "ready");
  const oldestOpenOrder = openOrders
    .filter((order) => order.status !== "ready")
    .reduce<string | null>((oldest, order) => (oldest === null || order.created_at < oldest ? order.created_at : oldest), null);
  const oldestWaitMinutes = oldestOpenOrder ? minutesSince(oldestOpenOrder, Date.now()) : 0;
  const overtime = sessions.filter((session) => session.venue_id === selectedVenue?.id && session.is_overtime);

  const liveUnits = selectedVenue?.units.filter((unit) => unit.current_session !== null).length ?? 0;
  const freeUnits = selectedVenue?.units.filter(
    (unit) => unit.current_session === null && unit.status !== "blocked" && unit.status !== "offline",
  ).length ?? 0;

  if (venues.length === 0) {
    const canCreateVenue = resolveProviderMembershipRole(summary, user) === "owner";
    return (
      <div className="ops-surface mx-auto grid w-full max-w-2xl gap-4 p-6 sm:p-8">
        {isLoading ? <p role="status">{t("Loading your venues...")}</p> : loadError ? (
          <>
            <h1 className="ops-page-title">{t("Load your workspace")}</h1>
            <p className="ops-copy" role="alert">{loadError}</p>
            <button className="ops-button" data-variant="primary" onClick={() => void reload()} type="button">{t("Retry")}</button>
          </>
        ) : (
          <>
            <p className="ops-kicker">{t("Your workspace is ready")}</p>
            <h1 className="ops-page-title">{t(canCreateVenue ? "Add your first venue" : "No venues assigned yet")}</h1>
            <p className="ops-copy">{t(canCreateVenue
              ? "Add your location and contact details. After venue approval, set up your gaming units and pricing to start taking bookings."
              : "Ask your venue owner to assign you a venue, then refresh your workspace.")}</p>
            <button className="ops-button" data-variant="primary" onClick={canCreateVenue ? openCreateVenue : () => void reload()} type="button">
              {t(canCreateVenue ? "Add venue" : "Refresh workspace")}
            </button>
          </>
        )}
      </div>
    );
  }

  // One row per kind of problem, never one per item, so the list stays short however busy it gets.
  const attention: AttentionRow[] = [];
  if (pendingConfirmationCount > 0) {
    attention.push({
      key: "bookings",
      icon: CalendarClock,
      tone: "warning",
      title: t(pendingConfirmationCount === 1 ? "{count} booking to confirm" : "{count} bookings to confirm", { count: pendingConfirmationCount }),
      detail: t("Players are waiting for a yes or no."),
      action: t("Review"),
      onAction: () => {
        setPendingReviewToken((current) => current + 1);
        navigateToSection("schedule");
      },
    });
  }
  if (openOrders.length > 0) {
    attention.push({
      key: "orders",
      icon: ChefHat,
      tone: oldestWaitMinutes >= SLOW_ORDER_MINUTES ? "danger" : "warning",
      title: t(openOrders.length === 1 ? "{count} food order open" : "{count} food orders open", { count: openOrders.length }),
      detail: oldestOpenOrder ? t("Oldest waiting {count} min", { count: oldestWaitMinutes }) : t("Ready to serve"),
      action: t("Open orders"),
      onAction: () => navigateToSection("orders"),
    });
  }
  if (overtime.length > 0) {
    attention.push({
      key: "overtime",
      icon: Hourglass,
      tone: "warning",
      title: t(overtime.length === 1 ? "{count} session over time" : "{count} sessions over time", { count: overtime.length }),
      detail: namesWithMore(overtime.map((session) => session.unit_name), t),
      action: t("Open floor"),
      onAction: () => navigateToSection("floor"),
    });
  }
  const restockCount = soldOut.length + lowStock.length;
  if (restockCount > 0) {
    attention.push({
      key: "stock",
      icon: PackageX,
      tone: soldOut.length > 0 ? "danger" : "warning",
      title: t(restockCount === 1 ? "{count} item needs restocking" : "{count} items need restocking", { count: restockCount }),
      detail: [
        soldOut.length > 0 ? t("Sold out: {names}", { names: namesWithMore(soldOut.map((item) => item.name), t) }) : null,
        lowStock.length > 0 ? t("Low: {names}", { names: namesWithMore(lowStock.map((item) => item.name), t) }) : null,
      ].filter(Boolean).join(" · "),
      action: t("Restock"),
      onAction: () => navigateToSection("menu"),
    });
  }

  const comingUp = incomingBookings.slice(0, COMING_UP_LIMIT);
  const bookingTime = (iso: string) => new Date(iso).toLocaleString(language === "ar" ? "ar-EG" : "en-US", {
    timeZone: selectedVenue?.timezone,
    weekday: "short",
    hour: "numeric",
    minute: "2-digit",
  });

  if (!selectedVenue) return null;

  return (
    <div className="grid gap-3">
      <section className="ops-surface home-header" aria-label={t("Today")}>
        <div className="home-status-row">
          <VenueStatusPill canManage={resolveProviderMembershipRole(summary, user) === "owner"} venue={selectedVenue} />
          <ShiftPill isOnline={isOnline} venue={selectedVenue} />
          <button className="home-pill" data-testid="floor-pill" onClick={() => navigateToSection("floor")} type="button">
            <Grid3X3 aria-hidden="true" size={16} />
            <span className="home-pill-text">
              <span className="home-pill-title">{t("{live} of {total} playing", { live: liveUnits, total: selectedVenue.units.length })}</span>
              <span className="home-pill-sub">{t(freeUnits === 1 ? "{count} unit free" : "{count} units free", { count: freeUnits })}</span>
            </span>
            <ChevronRight aria-hidden="true" className="home-pill-chevron" size={16} />
          </button>
        </div>
        <div className="home-actions">
          <button className="ops-button" data-variant="primary" disabled={selectedVenue.units.length === 0} onClick={openCreateBooking} type="button">
            {t("New booking")}
          </button>
          <button className="ops-button" data-variant="secondary" disabled={freeUnits === 0} onClick={openWalkIn} type="button">
            {t("Walk-in")}
          </button>
          <button className="ops-button" data-variant="secondary" onClick={() => navigateToSection("orders")} type="button">
            {t("Food order")}
          </button>
        </div>
      </section>

      <section className="ops-surface grid gap-2" aria-labelledby="home-attention-title">
        <h2 className="home-section-title" id="home-attention-title">{t("Needs attention")}</h2>
        {attention.length === 0 ? (
          <p className="home-all-clear"><CircleCheck aria-hidden="true" size={16} /> {t("Nothing needs you right now.")}</p>
        ) : (
          <ul className="home-attention" data-testid="home-attention">
            {attention.map((row) => (
              <li key={row.key}>
                <button className="home-attention-row" data-tone={row.tone} onClick={row.onAction} type="button">
                  <row.icon aria-hidden="true" className="home-attention-icon" size={18} />
                  <span className="min-w-0 flex-1">
                    <span className="home-attention-title">{row.title}</span>
                    {row.detail ? <span className="home-attention-detail">{row.detail}</span> : null}
                  </span>
                  <span className="home-attention-action">{row.action}</span>
                  <ChevronRight aria-hidden="true" className="home-pill-chevron" size={16} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="home-columns">
        <section className="ops-surface grid content-start gap-2" aria-labelledby="home-coming-up-title">
          <div className="flex items-center justify-between gap-2">
            <h2 className="home-section-title" id="home-coming-up-title">{t("Coming up")}</h2>
            <button className="ops-button" data-size="sm" data-variant="secondary" onClick={() => navigateToSection("schedule")} type="button">
              {t("All bookings")}
            </button>
          </div>
          {incomingLoading ? (
            <p className="ops-copy text-sm">{t("Loading...")}</p>
          ) : comingUp.length === 0 ? (
            <p className="ops-copy text-sm">{t("No upcoming bookings.")}</p>
          ) : (
            <ul className="home-list">
              {comingUp.map((booking) => (
                <li key={booking.id}>
                  <button
                    className="home-list-row"
                    onClick={() => navigate(`/provider/schedule?booking_id=${encodeURIComponent(booking.id)}&venue_id=${encodeURIComponent(selectedVenue.id)}`)}
                    type="button"
                  >
                    <span className="home-list-time">{bookingTime(booking.scheduled_start_at)}</span>
                    <span className="home-list-main">
                      <span className="truncate font-semibold">{booking.customer_name}</span>
                      <span className="ops-copy truncate text-xs">{booking.unit_name}</span>
                    </span>
                    <span className="ops-chip shrink-0" data-tone={getBookingStatusChipTone(booking.status)}>{t(formatLabel(booking.status, t))}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="ops-surface grid content-start gap-2" aria-labelledby="home-money-title" data-testid="provider-financial-summary">
          <div className="flex items-center justify-between gap-2">
            <h2 className="home-section-title" id="home-money-title">{t("Today's money")}</h2>
            <button aria-expanded={moneyOpen} className="ops-button" data-size="sm" data-variant="secondary" onClick={() => setMoneyOpen((value) => !value)} type="button">
              {moneyOpen ? t("Hide details") : t("Details")}
            </button>
          </div>
          {money ? (
            <dl className="home-money">
              <div><dt>{t("Billed")}</dt><dd>{formatEgp(money.issued_total_amount, language)}</dd></div>
              <div><dt>{t("Paid")}</dt><dd>{formatEgp(money.paid_total_amount, language)}</dd></div>
              <div data-tone={money.unpaid_total_amount > 0 ? "warning" : undefined}><dt>{t("Unpaid")}</dt><dd>{formatEgp(money.unpaid_total_amount, language)}</dd></div>
            </dl>
          ) : (
            <p className="ops-copy text-sm">{t("Loading...")}</p>
          )}
          {moneyOpen ? <ProviderReportsCard selectedVenue={selectedVenue} /> : null}
        </section>
      </div>
    </div>
  );
}

/** Today's billed / paid / unpaid for the summary line. Refreshes with shifts and every minute. */
function useTodayMoney(venueId: string | null) {
  const [report, setReport] = useState<DailyCloseoutReportRecord | null>(null);
  useEffect(() => {
    setReport(null);
    if (!venueId) return;
    let cancelled = false;
    const load = () => {
      providerApi.getDailyCloseoutReport({ venueId })
        .then((next) => { if (!cancelled) setReport(next); })
        .catch(() => undefined);
    };
    load();
    const id = setInterval(load, 60000);
    const unsubscribe = subscribeShiftChanged(load);
    return () => { cancelled = true; clearInterval(id); unsubscribe(); };
  }, [venueId]);
  return report;
}
